/**
 * WB3-5 — Prescriptive AI layer (suggest API ids → registry → confirm → ActionExecutor).
 * WB3-L6 — AI never auto-executes; user must approve each action.
 * WB4.5 — suggest API suggestedActionIds resolve via resolveWorkbenchActionById SSOT.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Popconfirm, Space, Typography } from 'antd';
import type { UniversalBaseEntity } from '@shared/types/entity';
import type { ObjectRef } from '@shared/business-workbench/workbench-focus';
import {
  emitAiSuggestUnknownActionTelemetry,
  augmentWorkbenchAiSuggestedSurfaces,
  projectActions,
  resolveAiSuggestedActionsFromIds,
  resolveWorkbenchActionById,
  type WorkbenchActionV3,
} from '@shared/business-workbench';
import { useOptionalDerivedWorkbenchFocus } from '../../../hooks/workbench/useOptionalDerivedWorkbenchFocus';
import { useAssistiveAI } from '@frontend/domain-hooks/conscious/useAssistiveAI';
import { useDetailWorkbenchActions } from '../../../hooks/workbench/useDetailWorkbenchActions';
import { ROLES } from '@shared/types/roles';
import { type DetailKernelHandlerOptions } from '../ActionExecutor';
import { dispatchWorkbenchKernelHandlerWithGateResult } from '../workbench-gated-kernel-dispatch';
import { applyWorkbenchKernelDispatchFeedback } from '../workbench-kernel-dispatch-feedback';
import { buildAssistiveGatewayPayload } from './build-assistive-payload';
import { omitUndefined } from '@shared/utils/omitUndefined';
import styles from './PrescriptiveActionsSlot.module.css';

const { Text } = Typography;

export interface PrescriptiveActionsSlotProps {
  entity: UniversalBaseEntity;
  businessDomain: string;
  enabled?: boolean;
  kernelHandlers?: DetailKernelHandlerOptions;
  role?: (typeof ROLES)[keyof typeof ROLES];
}

function PrescriptiveActionRow({
  action,
  preview,
  onApprove,
  disabledReason,
}: {
  action: WorkbenchActionV3;
  preview?: string;
  onApprove: (action: WorkbenchActionV3) => void;
  disabledReason?: string;
}) {
  const denied =
    action.availability === 'disabled' || Boolean(action.forbidden) || Boolean(disabledReason);

  return (
    <div className={styles.item} data-testid={`ai-prescriptive-${action.id}`}>
      {preview ? <p className={styles.preview}>{preview}</p> : null}
      {disabledReason ? (
        <Text type="secondary" data-testid={`ai-prescriptive-disabled-${action.id}`}>
          {disabledReason}
        </Text>
      ) : null}
      <Popconfirm
        title={`"${action.label}" 실행을 승인하시겠습니까?`}
        okText="승인 후 실행"
        cancelText="취소"
        disabled={denied}
        onConfirm={() => onApprove(action)}
      >
        <Button
          type="primary"
          size="small"
          disabled={denied}
          data-testid={`ai-prescriptive-approve-${action.id}`}
        >
          {action.label}
        </Button>
      </Popconfirm>
    </div>
  );
}

function UnknownActionPreviewRow({ actionId, preview }: { actionId: string; preview?: string }) {
  return (
    <div className={styles.item} data-testid={`ai-prescriptive-unknown-${actionId}`}>
      {preview ? <p className={styles.preview}>{preview}</p> : null}
      <Text type="secondary" data-testid={`ai-prescriptive-unknown-label-${actionId}`}>
        알 수 없는 액션 ({actionId}) — 레지스트리 매핑 전, 미리보기만 제공됩니다.
      </Text>
    </div>
  );
}

export function PrescriptiveActionsSlot({
  entity,
  businessDomain,
  enabled = true,
  kernelHandlers,
  role = ROLES.OPERATIONS_MANAGER,
}: PrescriptiveActionsSlotProps) {
  const derivedFocus = useOptionalDerivedWorkbenchFocus();
  const { actions } = useDetailWorkbenchActions({
    tenantId: entity.tenantId,
    entity,
    businessDomain,
    role,
    surface: 'inspector',
  });
  const { suggestResult, suggest, loading } = useAssistiveAI(entity.tenantId);
  const [suggestRequested, setSuggestRequested] = useState(false);

  const registryAiSuggested = useMemo(
    () =>
      projectActions(augmentWorkbenchAiSuggestedSurfaces(actions, businessDomain), 'inspector')
        .aiSuggested,
    [actions, businessDomain]
  );

  const apiResolution = useMemo(
    () => resolveAiSuggestedActionsFromIds(suggestResult?.suggestedActionIds, actions),
    [actions, suggestResult?.suggestedActionIds]
  );

  const displayActions = useMemo(() => {
    const byId = new Map<string, WorkbenchActionV3>();
    for (const action of registryAiSuggested) {
      byId.set(action.id, action);
    }
    for (const action of apiResolution.resolved) {
      byId.set(action.id, action);
    }
    return [...byId.values()];
  }, [apiResolution.resolved, registryAiSuggested]);

  const objectRef = useMemo(
    (): ObjectRef => ({
      id: entity.id,
      businessDomain,
      ...(entity.name ? { label: entity.name } : {}),
    }),
    [entity, businessDomain]
  );

  const handleApprove = useCallback(
    (action: WorkbenchActionV3): void => {
      if (!kernelHandlers) {
        return;
      }
      const registryAction = resolveWorkbenchActionById(action.id, actions);
      if (!registryAction || registryAction.forbidden) {
        return;
      }
      const actionPayload = {
        id: registryAction.id,
        label: registryAction.label,
        handlerRef: registryAction.handlerRef,
      };
      const result = dispatchWorkbenchKernelHandlerWithGateResult(actionPayload, {
        tenantId: entity.tenantId,
        objectRef,
        surface: 'ai',
        entitySnapshot: entity,
        onEnterEditMode: kernelHandlers.onEdit,
        ...(kernelHandlers.onDelete ? { onDelete: kernelHandlers.onDelete } : {}),
        ...(kernelHandlers.onStartFlow ? { onStartFlow: kernelHandlers.onStartFlow } : {}),
        flowStartContext: { entityId: entity.id, businessDomain },
      });
      applyWorkbenchKernelDispatchFeedback(result, actionPayload);
    },
    [actions, businessDomain, entity, kernelHandlers, objectRef]
  );

  const missingHandlerReason = kernelHandlers
    ? undefined
    : '실행 핸들러가 연결되지 않아 승인할 수 없습니다.';

  useEffect(() => {
    if (!enabled || suggestRequested || registryAiSuggested.length > 0) {
      return;
    }
    setSuggestRequested(true);
    void suggest({
      taskName: `${businessDomain} next step`,
      sourceText:
        buildAssistiveGatewayPayload(
          omitUndefined({
            tenantId: entity.tenantId,
            entity: {
              id: entity.id,
              name: entity.name,
              status: String(entity.status),
              businessDomain,
            },
            focus: derivedFocus,
          })
        ).sourceText ?? `${businessDomain}/${entity.id}`,
    });
  }, [
    businessDomain,
    derivedFocus,
    enabled,
    entity,
    registryAiSuggested.length,
    suggest,
    suggestRequested,
  ]);

  useEffect(() => {
    if (apiResolution.unknownIds.length === 0) {
      return;
    }
    emitAiSuggestUnknownActionTelemetry({
      kind: 'ai_suggest_unknown_action_id',
      unknownIds: apiResolution.unknownIds,
      ...(suggestResult?.suggestionId !== undefined
        ? { suggestionId: suggestResult.suggestionId }
        : {}),
      businessDomain,
    });
  }, [apiResolution.unknownIds, businessDomain, suggestResult?.suggestionId]);

  if (!enabled) {
    return null;
  }

  const draftPreview = suggestResult?.taskDraft?.summary;
  const showDraftOnly =
    displayActions.length === 0 && apiResolution.unknownIds.length === 0 && Boolean(draftPreview);

  return (
    <div className={styles.root} data-testid="ai-prescriptive-slot">
      <Text strong>AI 제안 액션</Text>
      <Space direction="vertical" size="small" {...(styles.root ? { className: styles.root } : {})}>
        {displayActions.map((action) => (
          <PrescriptiveActionRow
            key={action.id}
            action={action}
            onApprove={handleApprove}
            {...(missingHandlerReason ? { disabledReason: missingHandlerReason } : {})}
          />
        ))}
        {apiResolution.unknownIds.map((actionId) => (
          <UnknownActionPreviewRow
            key={actionId}
            actionId={actionId}
            {...(draftPreview ? { preview: draftPreview } : {})}
          />
        ))}
        {showDraftOnly ? (
          <div data-testid="ai-prescriptive-draft">
            <p className={styles.preview}>{draftPreview}</p>
            <Text type="secondary">커널 액션 매핑 전 — 미리보기만 제공됩니다.</Text>
          </div>
        ) : null}
        {displayActions.length === 0 &&
        apiResolution.unknownIds.length === 0 &&
        !draftPreview &&
        !loading ? (
          <Text type="secondary">현재 제안 가능한 액션이 없습니다.</Text>
        ) : null}
      </Space>
    </div>
  );
}
